import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  type ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from '@sea-snag/ui';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ArrowDown, ArrowUp, BarChart3, Layers, Target } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Area, AreaChart, CartesianGrid, Cell, Line, Pie, PieChart, XAxis, YAxis } from 'recharts';

import { useOrgParam } from '../../hooks/use-org-param';
import { analyticsApi, compSetsApi } from '../../lib/api-client';
import { queryKeys } from '../../lib/query-keys';
import { StatCard } from '../stat-card';

const MONTH_LABELS: Record<number, string> = {
  1: 'Jan',
  2: 'Feb',
  3: 'Mar',
  4: 'Apr',
  5: 'May',
  6: 'Jun',
  7: 'Jul',
  8: 'Aug',
  9: 'Sep',
  10: 'Oct',
  11: 'Nov',
  12: 'Dec',
};

const POSITION_COLORS = {
  below: 'var(--chart-1)',
  at: 'var(--chart-4)',
  above: 'var(--chart-3)',
};

const priceVsMarketConfig = {
  marketMaxPrice: { label: 'Market Max', color: 'var(--chart-2)' },
  marketMinPrice: { label: 'Market Min', color: 'var(--chart-2)' },
  marketAvgPrice: { label: 'Market Avg', color: 'var(--chart-4)' },
  yourAvgPrice: { label: 'Your Price', color: 'var(--chart-1)' },
} satisfies ChartConfig;

const positionConfig = {
  below: { label: 'Below Market', color: POSITION_COLORS.below },
  at: { label: 'At Market (±5%)', color: POSITION_COLORS.at },
  above: { label: 'Above Market', color: POSITION_COLORS.above },
} satisfies ChartConfig;

interface CompetitorsTabProps {
  year: string;
  boatPid: string;
  compSetPid: string;
  onCompSetPidChange: (pid: string) => void;
}

export function CompetitorsTab({
  year,
  boatPid,
  compSetPid,
  onCompSetPidChange,
}: CompetitorsTabProps) {
  const orgPid = useOrgParam();
  const boatParam = boatPid === 'all' ? undefined : boatPid;
  const compSetParam = compSetPid === 'all' ? undefined : compSetPid;

  const { data: compSetsData, isLoading: compSetsLoading } = useQuery({
    queryKey: queryKeys.compSets.listTagged(orgPid, 'analytics-filter'),
    queryFn: () => compSetsApi.list({ limit: 100 }),
    enabled: !!orgPid,
  });

  const compSetsList = compSetsData?.data ?? [];

  // Filter comp sets to match selected boat
  const filteredCompSets = boatParam
    ? compSetsList.filter((cs) => cs.referenceBoats.some((rb) => rb.id === boatParam))
    : compSetsList;

  // Reset comp set selection when boat changes
  const prevBoatRef = useRef(boatPid);
  useEffect(() => {
    if (prevBoatRef.current !== boatPid) {
      prevBoatRef.current = boatPid;
      onCompSetPidChange('all');
    }
  }, [boatPid, onCompSetPidChange]);

  const { data: position, isLoading: positionLoading } = useQuery({
    queryKey: queryKeys.analytics.competitorPosition(orgPid, year, boatParam, compSetParam),
    queryFn: () =>
      analyticsApi.competitorPosition({
        year,
        boatPid: boatParam,
        compSetPid: compSetParam,
      }),
    enabled: !!orgPid,
  });

  const { data: byMonth, isLoading: byMonthLoading } = useQuery({
    queryKey: queryKeys.analytics.competitorByMonth(orgPid, year, boatParam, compSetParam),
    queryFn: () =>
      analyticsApi.competitorByMonth({
        year,
        boatPid: boatParam,
        compSetPid: compSetParam,
      }),
    enabled: !!orgPid,
  });

  const { data: compSetBreakdown, isLoading: breakdownLoading } = useQuery({
    queryKey: queryKeys.analytics.competitorCompSets(orgPid, year),
    queryFn: () => analyticsApi.competitorCompSets({ year }),
    enabled: !!orgPid,
  });

  // No comp sets empty state — only show after comp sets query has loaded
  if (!compSetsLoading && compSetsList.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-12">
          <Layers className="text-muted-foreground h-12 w-12" />
          <div className="text-center">
            <h3 className="text-lg font-medium">No Competitor Sets</h3>
            <p className="text-muted-foreground text-sm">
              Create a competitor set to start comparing your pricing against the market.
            </p>
          </div>
          <Button asChild>
            <Link to="/$orgPid/competitor-sets" params={{ orgPid }}>
              Create Competitor Set
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const pieData = position
    ? [
        { name: 'below', label: 'Below Market', value: position.periodsBelowMarket },
        { name: 'at', label: 'At Market (±5%)', value: position.periodsAtMarket },
        { name: 'above', label: 'Above Market', value: position.periodsAboveMarket },
      ].filter((d) => d.value > 0)
    : [];

  const deltaPositive = (position?.deltaPct ?? 0) > 0;

  return (
    <div className="space-y-6">
      {/* Comp Set Filter */}
      <div className="flex items-center gap-3">
        <Select value={compSetPid} onValueChange={onCompSetPidChange}>
          <SelectTrigger className="w-[250px]">
            <SelectValue placeholder="All comp sets" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All comp sets</SelectItem>
            {filteredCompSets.map((cs) => (
              <SelectItem key={cs.id} value={cs.id}>
                {cs.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI Cards */}
      {positionLoading ? (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[80px]" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard
            label="vs Market Avg"
            value={`${deltaPositive ? '+' : ''}${position?.deltaPct ?? 0}%`}
            icon={deltaPositive ? ArrowUp : ArrowDown}
            className={deltaPositive ? 'border-red-200' : 'border-green-200'}
          />
          <StatCard label="Active Comp Sets" value={position?.activeCompSets ?? 0} icon={Layers} />
          <StatCard
            label="Below Market"
            value={position?.periodsBelowMarket ?? 0}
            icon={ArrowDown}
          />
          <StatCard label="Above Market" value={position?.periodsAboveMarket ?? 0} icon={ArrowUp} />
        </div>
      )}

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Price vs Market Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-4 w-4" />
              Your Price vs Market
            </CardTitle>
            <CardDescription>Monthly price comparison with market range</CardDescription>
          </CardHeader>
          <CardContent>
            {byMonthLoading ? (
              <Skeleton className="h-[300px]" />
            ) : (
              <ChartContainer config={priceVsMarketConfig} className="h-[300px] w-full">
                <AreaChart data={byMonth ?? []}>
                  <defs>
                    <linearGradient id="fillMarketMax" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="5%"
                        stopColor="var(--color-marketMaxPrice)"
                        stopOpacity={0.35}
                      />
                      <stop
                        offset="95%"
                        stopColor="var(--color-marketMaxPrice)"
                        stopOpacity={0.05}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(v: string) => {
                      const m = Number(v.split('-')[1]);
                      return MONTH_LABELS[m] ?? '';
                    }}
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    tickMargin={8}
                    tickFormatter={(v: number) => `€${(v / 1000).toFixed(0)}k`}
                  />
                  <ChartTooltip
                    cursor={false}
                    content={
                      <ChartTooltipContent
                        indicator="dot"
                        labelFormatter={(v: string) => {
                          const m = Number(v.split('-')[1]);
                          return MONTH_LABELS[m] ?? v;
                        }}
                        formatter={(value, name, item, index) => {
                          const label =
                            priceVsMarketConfig[name as keyof typeof priceVsMarketConfig]?.label ??
                            name;
                          return (
                            <div className="flex flex-1 items-center justify-between gap-2">
                              <div className="flex items-center gap-2">
                                <div
                                  className="h-2.5 w-2.5 shrink-0 rounded-[2px]"
                                  style={{ backgroundColor: item.color }}
                                />
                                <span className="text-muted-foreground">{label}</span>
                              </div>
                              <span
                                className="text-foreground font-mono font-medium tabular-nums"
                                key={index}
                              >
                                €{Number(value).toLocaleString()}
                              </span>
                            </div>
                          );
                        }}
                      />
                    }
                  />
                  <Area
                    type="monotone"
                    dataKey="marketMaxPrice"
                    stroke="none"
                    fill="url(#fillMarketMax)"
                    legendType="none"
                  />
                  <Area
                    type="monotone"
                    dataKey="marketMinPrice"
                    stroke="none"
                    fill="var(--background)"
                    fillOpacity={1}
                    legendType="none"
                  />
                  <Line
                    type="monotone"
                    dataKey="marketAvgPrice"
                    stroke="var(--color-marketAvgPrice)"
                    strokeDasharray="5 5"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="yourAvgPrice"
                    stroke="var(--color-yourAvgPrice)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <ChartLegend content={<ChartLegendContent />} />
                </AreaChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>

        {/* Position Distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Price Position
            </CardTitle>
            <CardDescription>Distribution of months by market position</CardDescription>
          </CardHeader>
          <CardContent>
            {positionLoading ? (
              <Skeleton className="h-[300px]" />
            ) : pieData.length === 0 ? (
              <p className="text-muted-foreground py-8 text-center text-sm">
                No position data available.
              </p>
            ) : (
              <ChartContainer config={positionConfig} className="h-[300px] w-full">
                <PieChart>
                  <ChartTooltip
                    cursor={false}
                    content={<ChartTooltipContent nameKey="name" hideLabel />}
                  />
                  <Pie
                    data={pieData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                    nameKey="name"
                    label={({ label, value }: { label: string; value: number }) =>
                      `${label}: ${value}`
                    }
                  >
                    {pieData.map((entry) => (
                      <Cell
                        key={entry.name}
                        fill={POSITION_COLORS[entry.name as keyof typeof POSITION_COLORS]}
                      />
                    ))}
                  </Pie>
                  <ChartLegend content={<ChartLegendContent nameKey="name" />} />
                </PieChart>
              </ChartContainer>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Comp Set Breakdown Table */}
      {!breakdownLoading && (compSetBreakdown ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Comp Set Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium">Comp Set</th>
                    <th className="pb-2 font-medium">Boat</th>
                    <th className="pb-2 text-right font-medium">Your Avg</th>
                    <th className="pb-2 text-right font-medium">Market Avg</th>
                    <th className="pb-2 text-right font-medium">Market Min</th>
                    <th className="pb-2 text-right font-medium">Market Max</th>
                    <th className="pb-2 text-right font-medium">Delta</th>
                  </tr>
                </thead>
                <tbody>
                  {(compSetBreakdown ?? []).map((cs) => (
                    <tr key={cs.compSetPid} className="border-b last:border-0">
                      <td className="py-3">
                        <Link
                          to="/$orgPid/competitor-sets/$pid"
                          params={{ orgPid, pid: cs.compSetPid }}
                          className="font-medium text-blue-600 hover:underline"
                        >
                          {cs.compSetName}
                        </Link>
                      </td>
                      <td className="py-3">{cs.boatName}</td>
                      <td className="py-3 text-right font-medium">
                        €{cs.yourAvgPrice.toLocaleString()}
                      </td>
                      <td className="py-3 text-right">€{cs.marketAvgPrice.toLocaleString()}</td>
                      <td className="py-3 text-right">€{cs.marketMinPrice.toLocaleString()}</td>
                      <td className="py-3 text-right">€{cs.marketMaxPrice.toLocaleString()}</td>
                      <td className="py-3 text-right">
                        <Badge
                          variant={
                            cs.deltaPct < -5
                              ? 'default'
                              : cs.deltaPct > 5
                                ? 'destructive'
                                : 'secondary'
                          }
                        >
                          {cs.deltaPct > 0 ? '+' : ''}
                          {cs.deltaPct}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
